import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchArticleSuggestions } from './fetchArticleSuggestions';
import { type ArticleSuggestionsResponse } from './types';

export type {
  ArticleSuggestionItem,
  ArticleSuggestionsResponse,
} from './types';

type ArticleSuggestionsQueryParams = {
  limit?: number;
  query?: string;
  siteId: UUID | undefined;
  supplierId: UUID | undefined;
};

/**
 * Generates the query options for the article suggestions query so they can be
 * shared between the useQuery hook and prefetching.
 *
 * @param params - Required site and supplier IDs, optional search query and result limit (1–50, default 5).
 * @param options - Additional options for the query.
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_article_suggestions_delivery_note_suggestions_articles_get
 */
export const getDeliveryNoteArticleSuggestionsQueryOptions = <
  TData = ArticleSuggestionsResponse,
>({
  params: { limit, query, siteId, supplierId },
  options,
}: {
  params: ArticleSuggestionsQueryParams;
  options?: Omit<
    UseQueryOptions<ArticleSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const normalizedQuery = query?.trim() || undefined;

  return queryOptions({
    queryFn: supplierId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchArticleSuggestions(
            { limit, query: normalizedQuery, siteId, supplierId },
            { signal },
          )
      : skipToken,
    queryKey: queryKeysDeliveryNote.getArticleSuggestions(
      siteId ?? '',
      supplierId ?? '',
      limit,
      normalizedQuery,
    ),
    ...options,
  });
};

/**
 * React Query hook for fetching the top articles a supplier has previously
 * delivered to a site, ranked by number of distinct delivery notes.
 *
 * @param params - Required site and supplier IDs, optional search query and result limit (1–50, default 5).
 * @param options - Additional options for the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_article_suggestions_delivery_note_suggestions_articles_get
 */
export const useQueryDeliveryNoteArticleSuggestions = <
  TData = ArticleSuggestionsResponse,
>(
  params: ArticleSuggestionsQueryParams,
  options?: Omit<
    UseQueryOptions<ArticleSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getDeliveryNoteArticleSuggestionsQueryOptions<TData>({ options, params }),
  );
